import z from "zod";
import {
  RoofingNonrequestableReasonEnum,
  ProposalNonrequestableReasonEnum,
  AuroraNonrequestableReasonEnum,
  VisionNonrequestableReasonEnum,
  SoloNonrequestableReasonEnum,
  LightreachNonrequestableReasonEnum,
} from "rdx/modules/projects/enums";
import { ProposalStatusType } from "generated/vision-graphql";
import { EnterpriseCompModelEnum } from "rdx/modules/users/types/enums";

export const VisionDesignSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  deletedAt: z.string().nullish(),
  updatedAt: z.string(),
  activatedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  requestType: z.string().nullish(),
  designUserId: z.number().nullish(),
  designRequest: z
    .object({
      id: z.string(),
      priority: z.number(),
      createdAt: z.string(),
      timeAdded: z.number(),
      updatedAt: z.string(),
      deletedWhy: z.unknown().optional(),
      designUser: z
        .object({
          id: z.string(),
          user: z.object({
            id: z.string(),
            bio: z.string().nullable(),
            email: z.string(),
            phone: z.string().nullable(),
            lastName: z.string(),
            createdAt: z.string(),
            deletedAt: z.string().nullish(),
            firstName: z.string(),
            updatedAt: z.string(),
            permissions: z.array(z.string()),
            powurUserId: z.number(),
          }),
          createdAt: z.string(),
          deletedAt: z.string().nullish(),
          startedAt: z.string(),
          updatedAt: z.string(),
          completedAt: z.string().nullable(),
        })
        .nullable(),
      promisedAt: z.string().nullable(),
      rejectedAt: z.string().nullable(),
      abandonedAt: z.string().nullable(),
      completedAt: z.string().nullable(),
      designNotes: z.array(
        z.object({
          id: z.string(),
          reason: z.string(),
          message: z.string(),
          createdAt: z.string(),
          createdBy: z.object({
            id: z.string(),
            bio: z.string().nullable(),
            email: z.string(),
            phone: z.string(),
            lastName: z.string(),
            createdAt: z.string(),
            deletedAt: z.string().nullish(),
            firstName: z.string(),
            updatedAt: z.string(),
            permissions: z.array(z.string()),
            powurUserId: z.number(),
          }),
          deletedAt: z.string().nullish(),
          updatedAt: z.string(),
        }),
      ),
      escalatedAt: z.string().nullable(),
      pausedUntil: z.string().nullable(),
      claimedUntil: z.string().nullable(),
    })
    .nullable(),
  designRequestId: z.number().nullable(),
});

export const ProposalStatusTypeSchema = z.nativeEnum(ProposalStatusType);

const BaseVisionProposalSchema = z.object({
  id: z.union([z.number(), z.string()]),
  systemName: z.string(),
  customName: z.string().nullable().optional(),
  version: z.number(),
  url: z.null(),
  createdAt: z.string(),
  updatedAt: z.string(),
  rejection: z
    .object({
      rejectedAt: z.string(),
      detailsToChange: z.string().optional(),
      reasonForRevision: z.string().optional(),
      rejecter: z
        .object({
          id: z.number(),
          fullName: z.string(),
          email: z.string(),
        })
        .optional(),
    })
    .optional(),
  meta: z
    .object({
      id: z.string(),
      uuid: z.string().optional(),
      kind: z.string().optional(),
      projectId: z.number().optional(),
      slug: z.string().optional(),
      createdAt: z.string(),
      deletedAt: z.string().nullish(),
      updatedAt: z.string().optional(),
      site: z
        .object({
          id: z.string(),
          state: z.string(),
          street: z.string(),
          address: z.string(),
          latitude: z.number(),
          locality: z.string(),
          regionId: z.string().nullish(),
          createdAt: z.string(),
          deletedAt: z.string().optional(),
          longitude: z.number(),
          updatedAt: z.string(),
          postalCode: z.string(),
          postalCodeSuffix: z.string(),
        })
        .optional(),
      seller: z
        .object({
          id: z.string(),
          bio: z.string().nullable(),
          email: z.string(),
          phone: z.string(),
          lastName: z.string(),
          createdAt: z.string(),
          deletedAt: z.string().nullish(),
          firstName: z.string(),
          updatedAt: z.string(),
          permissions: z.array(z.string()),
          powurUserId: z.number(),
        })
        .optional(),
      sellerId: z.string().optional(),
      homeowner: z
        .object({
          id: z.string(),
          bio: z.string().nullish(),
          email: z.string(),
          phone: z.string(),
          lastName: z.string(),
          createdAt: z.string(),
          deletedAt: z.string().nullish(),
          firstName: z.string(),
          updatedAt: z.string(),
          permissions: z.array(z.string()).optional(),
          powurUserId: z.number().nullish(),
        })
        .optional(),
      homeownerId: z.string().nullish(),
      designs: z.array(VisionDesignSchema).optional(),
      status: ProposalStatusTypeSchema.nullish(),
      auroraDesignId: z.string().nullish(),
    })
    .nullish(),
});

export const VisionProposalSchema = BaseVisionProposalSchema.extend({
  kind: z.literal("vision"),
});

export const VisionLiteProposalSchema = BaseVisionProposalSchema.extend({
  kind: z.literal("vision_lite"),
});

export const VisionProposalPartialSchema = BaseVisionProposalSchema.partial();

export const SoloProposalSchema = z.object({
  kind: z.literal("solo"),
  id: z.number(),
  systemName: z.string(),
  customName: z.string().nullable().optional(),
  projectId: z.number(),
  typeVersion: z.number(),
  overallVersion: z.number(),
  version: z.number(),
  url: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  rejection: z
    .object({
      rejectedAt: z.string(),
      detailsToChange: z.string().optional(),
      reasonForRevision: z.string().optional(),
      rejecter: z
        .object({
          id: z.number(),
          fullName: z.string(),
          email: z.string(),
        })
        .optional(),
    })
    .optional(),
  meta: z
    .object({
      id: z.number(),
      lat: z.string(),
      lon: z.string(),
      path: z.string(),
      sent: z.string(),
      ppaOr: z.unknown(),
      module: z.object({
        id: z.number(),
        name: z.string(),
        type: z.number(),
        width: z.number(),
        length: z.number(),
        wattage: z.number(),
        manufacturer: z.object({ id: z.number(), name: z.string() }),
        companyModules: z.array(z.object({ perWattAdder: z.number() })),
        manufacturerId: z.number(),
      }),
      offset: z.string().nullable(),
      aprbill: z.string(),
      augbill: z.string(),
      decbill: z.string(),
      febbill: z.string(),
      finance: z.object({
        id: z.number(),
        rate: z.number().nullable(),
        term: z.number().nullable(),
        type: z.string(),
        lenderId: z.number(),
        displayName: z.string(),
      }),
      janbill: z.string(),
      julbill: z.string(),
      junbill: z.string(),
      marbill: z.string(),
      maybill: z.string(),
      novbill: z.string(),
      octbill: z.string(),
      sepbill: z.string(),
      solarimg: z.string(),
      aprPrep: z.number(),
      augPrep: z.number(),
      customer: z.object({
        id: z.number(),
        lat: z.string(),
        lon: z.string(),
        zip: z.string(),
        city: z.string(),
        user: z.object({
          id: z.number(),
          email: z.string(),
          crmId: z.number().nullable(),
          lastName: z.string(),
          companyId: z.number(),
          firstName: z.string(),
        }),
        email: z.string(),
        phone: z.string(),
        state: z.string(),
        address: z.string(),
        userId: z.number(),
        realtime: z.boolean(),
        lastName: z.string(),
        leadType: z.string().nullable(),
        companyId: z.number(),
        firstName: z.string(),
        leadNumber: z.string(),
        sameDayLabel: z.string().optional(),
        realtimeLabel: z.string().optional(),
      }),
      decPrep: z.number(),
      febPrep: z.number(),
      inverter: z.object({
        id: z.number(),
        type: z.string(),
        model: z.string(),
        manufacturer: z.object({ id: z.number(), name: z.string() }),
        manufacturerId: z.number(),
        companyInverters: z.array(z.object({ perWattAdder: z.number() })),
      }),
      janPrep: z.number(),
      julPrep: z.number(),
      junPrep: z.number(),
      marPrep: z.number(),
      mayPrep: z.number(),
      novPrep: z.number(),
      octPrep: z.number(),
      sepPrep: z.number(),
      firstNote: z.object({ note: z.string() }),
      moduleId: z.number(),
      overrides: z.object({}),
      batteryId: z.number().nullable(),
      companyId: z.number(),
      customerId: z.number(),
      financeId: z.number(),
      leadNumber: z.string(),
      proposalId: z.string(),
      utilityId: z.number(),
      externalId: z.string(),
      inProgress: z.boolean().optional(),
      inverterId: z.number(),
      loanAmount: z.string(),
      panelCount: z.number(),
      proposalPdf: z.string(),
      proposalRep: z.object({
        id: z.number(),
        email: z.string(),
        crmId: z.number().nullable(),
        manager: z.unknown(),
        lastName: z.string(),
        firstName: z.string(),
        managerId: z.number(),
        externalSalesRepId: z.number().nullable(),
      }),
      proposalUrl: z.string(),
      systemSize: z.string(),
      companyName: z.string(),
      dateCreated: z.string(),
      nearmapUsed: z.boolean().nullable(),
      batteryCount: z.number(),
      generatedDate: z.string(),
      pricePerKWh: z.string().nullable(),
      realtimeDate: z.string().nullable(),
      requestNotes: z.string().nullable(),
      updateReason: z.string(),
      companyCustom: z.unknown(),
      computedValues: z.unknown(),
      dateRequested: z.string(),
      googlemapUsed: z.boolean().nullable(),
      inverterCount: z.number(),
      pricePerWatt: z.string(),
      requestSource: z.string(),
      sketchDrawing: z.unknown(),
      monthlySavings: z.string(),
      proposalRequest: z.object({
        id: z.number(),
        usage: z.object({
          type: z.string(),
          annual: z.string(),
          monthly: z.array(z.unknown()),
        }),
        offset: z.number(),
        realtime: z.boolean(),
        createdAt: z.string(),
        updatedAt: z.string(),
        utilityId: z.number(),
        customerId: z.number(),
        disabledAt: z.string().nullable(),
        customPpkwh: z.unknown().nullable(),
        customUtility: z.unknown().nullable(),
        desiredOffset: z.boolean(),
        expectedTimeDue: z.unknown(),
        isSunpixelReady: z.boolean(),
        shouldUseSunpixel: z.boolean(),
      }),
      utilityCompany: z.object({ id: z.number(), name: z.string() }),
      valueQuestions: z.array(z.unknown()),
      requestPriority: z.string(),
      annualProduction: z.number(),
      longTermSavings: z.string().nullable(),
      nonBillableDate: z.string().nullable(),
      customerSignature: z.unknown().nullable(),
      firstYearSavings: z.string(),
      trueRequestedDate: z.string().nullable(),
      dateProposalSigned: z.string().nullable(),
      lastAccessByLegacyKey: z.unknown().nullable(),
    })
    .nullish(),
  revisionData: z.unknown().nullable(),
  rejecterId: z.number().nullable(),
  revisionCompletedAt: z.string().nullable(),
  s3ProposalData: z.unknown().nullable(),
  externalId: z.string(),
  hiddenAt: z.string().nullable(),
});

export const LightreachProposalSchema = SoloProposalSchema.extend({
  kind: z.literal("lightreach"),
});

export const FundingProposalSchema = z.object({
  kind: z.literal("funding"),
  id: z.number(),
  systemName: z.string(),
  customName: z.string().nullable().optional(),
  version: z.number(),
  url: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
  completedAt: z.string().nullable().optional(),
  assignedAt: z.string().nullable().optional(),
  statusDisplay: z.enum(["requested", "in_progress", "completed", "rejected", "escalated"]).optional(),
  fundingProposalUrl: z.string().optional(),
  fundingCompanySlug: z.enum(["everbright", "sunnova", "aurora"]).optional(),
  adminNotes: z.string().nullable(),
  proposalNotes: z.string().nullable().optional(),
  complete: z.boolean().optional(),
  rejection: z
    .object({
      rejectedAt: z.string(),
    })
    .optional(),
  meta: z.object({}).optional(),
});

export const ProposalUnionSchema = z.discriminatedUnion("kind", [
  SoloProposalSchema,
  LightreachProposalSchema,
  VisionProposalSchema,
  VisionLiteProposalSchema,
  FundingProposalSchema,
]);

export const BaseRoofingProposalSchema = z.object({
  id: z.number(),
  providerLeadId: z.number(),
  version: z.number(),
  assigneeId: z.number().nullable(),
  assignedAt: z.string().nullable(),
  pendingReason: z.string().nullable(),
  formData: z
    .object({
      reRoof: z.string(),
      woodRot: z.boolean(),
      underType: z.string(),
      otherNotes: z.string().nullable(),
      newRoofType: z.string(),
      flatRoofType: z.string().nullable(),
      existingRoofType: z.string(),
      otherExistingRoofType: z.string(),
    })
    .partial(),
  dueDate: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  roofingProposalUrl: z.string().nullable(),
  s3RoofingProposalData: z
    .object({
      key: z.string(),
      name: z.string(),
      bucket: z.string(),
      date_time: z.string(),
    })
    .partial(),
  measurementDocUrl: z.string().nullable(),
  s3MeasurementDocData: z
    .object({
      key: z.string(),
      name: z.string(),
      bucket: z.string(),
      date_time: z.string(),
    })
    .partial(),
  completedAt: z.string().nullable(),
  submittedById: z.number().nullable(),
  canceledAt: z.string().nullable(),
  proposalNotes: z.string().nullable(),
  measurementNotes: z.string().nullable(),
});

export const RoofingProposalSchema = z.object({
  latestProposal: BaseRoofingProposalSchema,
  roofingAssets: z.array(z.unknown()),
});

export const TpoPricingWorksheetSchema = z.object({
  id: z.number(),
  worksheetUrl: z.string(),
  projectId: z.number(),
  slug: z.string(),
  version: z.number(),
  createdAt: z.string(),
});

export const ProposalNonRequestableReasonSchema = z.nativeEnum(ProposalNonrequestableReasonEnum);
export const RoofingNonRequestableReasonSchema = z.nativeEnum(RoofingNonrequestableReasonEnum);
export const AuroraNonrequestableReasonSchema = z.nativeEnum(AuroraNonrequestableReasonEnum);
export const VisionNonrequestableReasonSchema = z.nativeEnum(VisionNonrequestableReasonEnum);
export const SoloNonrequestableReasonSchema = z.nativeEnum(SoloNonrequestableReasonEnum);
export const LightreachNonrequestableReasonSchema = z.nativeEnum(LightreachNonrequestableReasonEnum);

export const GetProjectProposalsParamsSchema = z.object({
  id: z.number(),
  enterprise: z.boolean().optional(),
});

export const GetProjectProposalsResponseSchema = z.object({
  status: z.number(),
  body: z.object({
    proposals: z.array(ProposalUnionSchema),
    visionProposal: VisionProposalPartialSchema.nullish(),
    proposalUrl: z.string().nullish(),
    canRequestSunnovaProposal: z.boolean(),
    sunnovaNonRequestableReason: z.string().nullable(),
    canRequestAuroraProposal: z.boolean(),
    auroraNonRequestableReason: z.string().nullable(),
  }),
});

export const RenameProposalParamsSchema = z.object({
  proposalId: z.number(),
  projectId: z.number(),
  name: z.string().max(255),
  isAdmin: z.boolean().optional(),
});

export const RenameProposalResponseSchema = z.object({
  status: z.number(),
  body: ProposalUnionSchema,
});

export const RequestRevisionParamsSchema = z.object({
  id: z.number(),
  reason: z.string(),
  note: z.string(),
  project_id: z.number(),
});

export const RequestRevisionResponseSchema = z.object({
  status: z.number(),
  body: SoloProposalSchema,
});

export const RequestVisionRevisionParamsSchema = z.object({
  reasons: z.array(z.string()),
  note: z.string(),
  parentId: z.string(),
  proposalId: z.string(),
  projectId: z.number(),
});

export const RequestVisionRevisionResponseSchema = z.object({
  requestDesignRevision: z.object({
    id: z.string(),
    createdAt: z.string(),
    deletedAt: z.string().nullish(),
    updatedAt: z.string(),
    activatedAt: z.string().nullable(),
    completedAt: z.string().nullable(),
    requestType: z.unknown(),
    designUserId: z.number().nullish(),
    designRequestId: z.number().nullable(),
  }),
});

export const LinkAuroraDesignParamsSchema = z.object({
  proposalId: z.number(),
  projectId: z.number(),
  auroraDesignId: z.string(),
});

export const LinkAuroraDesignResponseSchema = z.object({
  status: z.number(),
  body: z
    .object({
      id: z.string(),
      proposalId: z.string(),
      name: z.string().nullish(),
      auroraProjectId: z.string().nullish(),
      syncStatus: z.string().nullish(),
    })
    .passthrough(),
});

export const RefreshAuroraDesignParamsSchema = z.object({
  proposalId: z.number(),
  projectId: z.number(),
});

export const RefreshAuroraDesignResponseSchema = LinkAuroraDesignResponseSchema;

export const ProposalShowSchema = z.object({
  kind: z.enum(["solo", "lightreach", "vision", "vision_lite", "funding"]),
  systemName: z.string().nullable(),
  customName: z.string().nullable().optional(),
  id: z.union([z.number(), z.string()]),
  createdAt: z.string(),
  updatedAt: z.string(),
  version: z.union([z.number(), z.string()]),
  url: z.string().nullable(),
  meta: z.unknown().nullable().optional(),
  status: ProposalStatusTypeSchema.optional(),
  rejection: z
    .object({
      rejectedAt: z.string(),
      detailsToChange: z.string().optional(),
      reasonForRevision: z.string().optional(),
      rejecter: z
        .object({
          id: z.number(),
          fullName: z.string(),
          email: z.string(),
        })
        .optional(),
    })
    .optional(),
  statusDisplay: z.enum(["requested", "in_progress", "completed", "rejected", "escalated"]).optional(),
  fundingProposalUrl: z.string().nullable().optional(),
  fundingCompanySlug: z.enum(["everbright", "sunnova", "aurora"]).optional(),
  adminNotes: z.string().nullable().optional(),
  proposalNotes: z.string().nullable().optional(),
  escalationNotes: z.string().nullable().optional(),
  escalatedAt: z.string().nullable().optional(),
  completedAt: z.string().nullable().optional(),
  assignedAt: z.string().nullable().optional(),
  canceledAt: z.string().nullable().optional(),
  inProgressAt: z.string().nullable().optional(),
  dueAt: z.string().optional(),
  productType: z.string().optional(),
  fundingProductId: z.number().optional(),
  fundingProductLabel: z.string().optional(),
  targetEscalator: z.string().nullable().optional(),
  targetInterestRate: z.string().nullable().optional(),
  targetLoanLength: z.string().nullable().optional(),
  solarProposalUrl: z.string().nullable().optional(),
  complete: z.boolean().optional(),
  project: z
    .object({
      id: z.number(),
      fullName: z.string(),
      address: z.object({
        street: z.string(),
        unit: z.string().nullable(),
        city: z.string(),
        state: z.string(),
        zip: z.string(),
        country: z.string(),
      }),
      submitter: z.object({
        id: z.number(),
        fullName: z.string(),
        email: z.string(),
        phone: z.string(),
        avatar: z
          .object({
            large: z.string(),
            thumb: z.string(),
            retina: z.string(),
            preview: z.string(),
            original: z.string().optional(),
          })
          .optional(),
        projectRole: z.string(),
      }),
      enterprise: z
        .object({
          id: z.number(),
          name: z.string(),
          compType: z.nativeEnum(EnterpriseCompModelEnum),
        })
        .optional(),
    })
    .optional(),
  assignedTeamMember: z
    .object({
      id: z.number(),
      fullName: z.string(),
      avatarUrls: z.object({
        thumb: z.string().nullish(),
      }),
    })
    .nullish(),
  fundingProposal: z
    .object({
      name: z.string(),
      date: z.string(),
      link: z.string().optional(),
      type: z.literal("funding_proposal"),
    })
    .optional(),
  fundingFinanceOptions: z
    .object({
      productTypes: z.array(z.string()),
      financeProducts: z.array(
        z.object({
          id: z.number(),
          productLabel: z.string(),
          productType: z.string(),
          tooltipText: z.string().nullable(),
          options: z.object({
            id: z.number(),
            proposalFinanceProductId: z.number(),
            stateAbbr: z.string(),
            archivedAt: z.string().nullable(),
            escalators: z.array(z.string()),
            loanLength: z.array(z.string()),
            interestRate: z.array(z.string()),
            createdAt: z.string(),
            updatedAt: z.string(),
            productDescription: z.string().nullable(),
          }),
        }),
      ),
    })
    .optional(),
});

export const GetProposalDetailsParamsSchema = z.object({
  id: z.number(),
  proposalId: z.number(),
  isAdmin: z.boolean().optional(),
  enterprise: z.boolean().optional(),
});

export const GetProposalDetailsResponseSchema = z.object({
  status: z.number(),
  body: ProposalShowSchema,
});
